import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from './useUserRole';

export interface ProcessCounts {
  intake: number;
  scoring: number;
  approval: number;
  contracts: number;
  active: number;
  overdue: number;
}

const initialCounts: ProcessCounts = {
  intake: 0,
  scoring: 0,
  approval: 0,
  contracts: 0,
  active: 0,
  overdue: 0,
};

export function useProcessCounts() {
  const [counts, setCounts] = useState<ProcessCounts>(initialCounts);
  const [loading, setLoading] = useState(true);
  const { isAdmin } = useUserRole();

  const fetchCounts = useCallback(async () => {
    if (!isAdmin) {
      setCounts(initialCounts);
      setLoading(false);
      return;
    }

    try {
      const [loanRequestsResult, guestApplicationsResult, convertedGuestResult, activeLoansResult, overdueLoansResult] = await Promise.all([
        (supabase as any).from('loan_requests').select('id, status, risk_score'),
        (supabase as any).from('guest_applications').select('status, risk_score').in('status', ['pending', 'reviewing', 'scoring', 'approved']),
        (supabase as any)
          .from('guest_applications')
          .select('converted_loan_request_id')
          .eq('status', 'converted')
          .not('converted_loan_request_id', 'is', null),
        (supabase as any).from('loans').select('id', { count: 'exact', head: true }).eq('status', 'active'),
        (supabase as any).from('loans').select('id', { count: 'exact', head: true }).eq('status', 'overdue'),
      ]);

      if (loanRequestsResult.error) {
        console.error('Error fetching loan requests:', loanRequestsResult.error);
        return;
      }

      if (convertedGuestResult.error) {
        console.error('Error fetching converted guest applications:', convertedGuestResult.error);
        return;
      }

      const convertedLoanRequestIds = new Set(
        (convertedGuestResult.data || [])
          .map((app: { converted_loan_request_id: string | null }) => app.converted_loan_request_id)
          .filter(Boolean)
      );

      const newCounts: ProcessCounts = {
        intake: 0,
        scoring: 0,
        approval: 0,
        contracts: 0,
        active: 0,
        overdue: 0,
      };

      loanRequestsResult.data?.forEach((request: { id: string; status: string; risk_score: number | null }) => {
        switch (request.status) {
          case 'new':
            newCounts.intake++;
            break;
          case 'review':
            if (request.risk_score === null) {
              newCounts.scoring++;
            } else {
              newCounts.approval++;
            }
            break;
          case 'approved':
            if (convertedLoanRequestIds.has(request.id)) {
              newCounts.contracts++;
            } else {
              newCounts.approval++;
            }
            break;
          case 'contract_pending':
          case 'contract_sent':
            newCounts.contracts++;
            break;
        }
      });

      // active/overdue counted from loans table (source of truth) so deleting
      // a loan immediately drops the badge even if loan_request still says 'active'
      newCounts.active = activeLoansResult.count || 0;
      newCounts.overdue = overdueLoansResult.count || 0;

      if (!guestApplicationsResult.error && guestApplicationsResult.data) {
        guestApplicationsResult.data.forEach((app: { status: string; risk_score: number | null }) => {
          if (app.status === 'pending' || app.status === 'reviewing') {
            newCounts.intake++;
          } else if (app.status === 'scoring') {
            if (app.risk_score === null) {
              newCounts.scoring++;
            } else {
              newCounts.approval++;
            }
          } else if (app.status === 'approved') {
            // Approved guest app waits for admin to convert (create user account)
            newCounts.approval++;
          }
        });
      }

      setCounts(newCounts);
    } catch (error) {
      console.error('Error calculating process counts:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchCounts();

    // Non-admins (clients) don't need realtime updates on admin-process tables
    if (!isAdmin) return;

    // Unique channel names per hook instance — Sidebar can be mounted twice
    // (desktop + mobile drawer) and Supabase Realtime errors on duplicate names:
    // "cannot add postgres_changes callbacks after subscribe()".
    const id = Math.random().toString(36).slice(2, 8);

    const loanChannel = supabase
      .channel(`loan_requests_changes_${id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'loan_requests' },
        () => { fetchCounts(); }
      )
      .subscribe();

    const guestChannel = supabase
      .channel(`guest_applications_changes_${id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'guest_applications' },
        () => { fetchCounts(); }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(loanChannel);
      supabase.removeChannel(guestChannel);
    };
  }, [fetchCounts, isAdmin]);

  return { counts, loading, refetch: fetchCounts };
}
